import * as React from 'react';

import { RECORD_VISIT_KINDS, type IRecordVisit } from '@/design';

/**
 * The records a staff member opened last — people, firms, responses, accounts — for the header's
 * "Recent" menu and the Team Portal's home (the two-portals design, §4.3).
 *
 * **Browser storage, and only as a convenience.** Per viewer, keyed by account so that two people
 * sharing a browser never see each other's, cleared when that account signs out from the rail's
 * menu, and eight at most. Each entry is what the page last called the record and the address that
 * opens it — no field of the record itself. Every read and write is guarded: a private window, a
 * blocked store or a full one leaves the list empty, and nothing on the page depends on it.
 */

const MAX = 8;
const keyOf = (accountId: string) => `veritant.team.recent.${accountId}`;

function read(accountId: Nullable<string>): IRecordVisit[] {
  if (!accountId) return [];
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(keyOf(accountId)) ?? '[]');
    return Array.isArray(parsed) ? parsed.map(registerFirm).filter(isVisit).slice(0, MAX) : [];
  } catch {
    return [];
  }
}

function write(accountId: string, records: IRecordVisit[]): void {
  try {
    window.localStorage.setItem(keyOf(accountId), JSON.stringify(records));
  } catch {
    // Storage refused: the list lives in memory for this page load, which is all it promises.
  }
}

function isVisit(value: unknown): value is IRecordVisit {
  const candidate = value as Partial<IRecordVisit> | null;
  return (
    typeof candidate?.path === 'string' &&
    candidate.path.startsWith('/team/') &&
    typeof candidate.label === 'string' &&
    (RECORD_VISIT_KINDS as readonly unknown[]).includes(candidate.kind)
  );
}

/** A visit stored before SR-414, when `firm` named a register firm's card: it is still one, and
 *  says so — `firm` names a Firms record now. */
function registerFirm(value: unknown): unknown {
  const candidate = value as Partial<IRecordVisit> | null;
  return candidate?.kind === 'firm' &&
    typeof candidate.path === 'string' &&
    candidate.path.startsWith('/team/sec-adv/')
    ? { ...candidate, kind: 'register-firm' }
    : value;
}

export interface IRecentRecords {
  records: IRecordVisit[];
  add: (record: IRecordVisit) => void;
  clear: () => void;
}

export function useRecentRecords(accountId: Nullable<string>): IRecentRecords {
  const [records, setRecords] = React.useState<IRecordVisit[]>(() => read(accountId));

  React.useEffect(() => setRecords(read(accountId)), [accountId]);

  const add = React.useCallback(
    (record: IRecordVisit) => {
      if (!accountId) return;
      setRecords((current) => {
        const next = [record, ...current.filter((held) => held.path !== record.path)].slice(0, MAX);
        write(accountId, next);
        return next;
      });
    },
    [accountId]
  );

  const clear = React.useCallback(() => {
    if (accountId) {
      try {
        window.localStorage.removeItem(keyOf(accountId));
      } catch {
        // Nothing to undo: an unreadable store held nothing this page could show.
      }
    }
    setRecords([]);
  }, [accountId]);

  return { records, add, clear };
}
